/**
 * useRemoteCommandPoller — commandes d'assistance à distance (module Assistance super_admin).
 *
 * Toutes les 30 s, le terminal demande ses commandes en disant si la caisse est inactive
 * (panier vide, aucune fenêtre, écran pas touché depuis 2 min). Caisse occupée, le serveur
 * ne livre que ping et capture : kill, redémarrage, mise à jour, rechargement attendent.
 *
 * L'accusé part AVANT toute action qui ferme ou recharge la page ; sinon la commande
 * restait « sent » sans résultat.
 */

import { useEffect, useRef } from 'react';
import { DeviceRemoteApi } from '@/api/devices/DeviceRemoteApi';
import { UsersApi } from '@/api/users';
import { createBrowserIdleProbe, evaluateCaisseIdle } from '@/lib/remote-commands/caisse-idle';
import {
  NON_DISRUPTIVE_COMMANDS,
  planCommand,
  type CommandEnv,
  type PendingCommand,
  type RemoteOpsBridge,
  type UpdaterBridge,
} from '@/lib/remote-commands/command-executor';

const POLL_INTERVAL_MS = 30 * 1000;
const DEVICE_ID_STORAGE_KEY = 'visionpos.device.id';

type BridgeWindow = Window & {
  VisionPOSRemoteOps?: RemoteOpsBridge;
  VisionPOSUpdater?: UpdaterBridge;
};

function readDeviceId(): string | null {
  try {
    return localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

function browserEnv(): CommandEnv {
  const w = window as BridgeWindow;
  let storage: Storage | null = null;
  try {
    storage = window.localStorage;
  } catch {
    storage = null;
  }
  return {
    remote: w.VisionPOSRemoteOps ?? null,
    updater: w.VisionPOSUpdater ?? null,
    storage,
    userAgent: navigator.userAgent || '',
    reloadPage: () => window.setTimeout(() => window.location.reload(), 200),
    signOutLocal: () => DeviceRemoteApi.signOutThisDevice().catch(() => undefined),
    goToAuth: () => {
      window.location.href = '/auth';
    },
  };
}

async function resolveTenantId(): Promise<string | null> {
  const session = await UsersApi.getSession();
  if (!session.ok || !session.value.user_id) return null;
  const tenant = await UsersApi.getMyTenantId();
  return tenant.ok ? tenant.value : null;
}

/** Exécute une commande : accusé d'abord, puis l'action qui peut fermer la page. */
async function runCommand(cmd: PendingCommand, idle: boolean): Promise<void> {
  // Double garde côté terminal, même si le serveur a déjà filtré.
  if (!idle && !NON_DISRUPTIVE_COMMANDS.has(cmd.command_type)) {
    await DeviceRemoteApi.ackCommand(cmd.id, false, { reason: 'caisse occupée' });
    return;
  }
  const plan = planCommand(cmd, browserEnv());
  await DeviceRemoteApi.ackCommand(cmd.id, plan.ok, plan.result);
  if (plan.ok && plan.run) await plan.run();
}

export function useRemoteCommandPoller(): void {
  const inFlightRef = useRef(false);
  const tenantIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const probe = createBrowserIdleProbe();

    const tick = async () => {
      if (cancelled || inFlightRef.current) return;
      const deviceId = readDeviceId();
      if (!deviceId) return;
      inFlightRef.current = true;
      try {
        // Démarré avant la connexion : le commerce est résolu dès qu'une session existe.
        if (!tenantIdRef.current) tenantIdRef.current = await resolveTenantId();
        const tenantId = tenantIdRef.current;
        if (!tenantId) return;
        const idle = evaluateCaisseIdle(probe).idle;
        const pulled = await DeviceRemoteApi.pullCommands(deviceId, tenantId, idle);
        if (!pulled.ok) {
          // Déconnexion ou changement de commerce : le commerce sera relu au prochain tour.
          tenantIdRef.current = null;
          return;
        }
        for (const cmd of pulled.value) {
          if (cancelled) return;
          await runCommand(cmd, idle);
        }
      } catch (e) {
        console.warn('[RemoteCommandPoller] tick failed:', e instanceof Error ? e.message : e);
      } finally {
        inFlightRef.current = false;
      }
    };

    void tick();
    const intervalId = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);
}
